import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  supplierService,
  Supplier,
  type CreateSupplierRequest,
  type UpdateSupplierRequest,
} from '@/services/supplierService';

export const useSuppliers = () => {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: () => supplierService.getAll().then(res => res.data),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSupplier = (id: number) => {
  return useQuery({
    queryKey: ['suppliers', id],
    queryFn: () => supplierService.getById(id).then(res => res.data),
    enabled: !!id,
  });
};

export const useSupplierSearch = (search: string, limit: number = 20) => {
  return useQuery({
    queryKey: ['suppliers', 'search', search, limit],
    queryFn: ({ signal }) => supplierService.search(search, limit, signal).then(res => res.data),
    staleTime: 30 * 1000, // 30 seconds for search results
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
};

export const useSupplierInfiniteSearch = (search: string, pageSize: number = 50) => {
  return useInfiniteQuery({
    queryKey: ['suppliers', 'search-paged', search, pageSize],
    queryFn: ({ pageParam = 1, signal }) =>
      supplierService.searchPaged(search, pageParam, pageSize, signal).then(res => res.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? lastPage.pageNumber + 1 : undefined,
    staleTime: 30 * 1000,
  });
};

export const useSupplierPage = (search: string, pageNumber: number, pageSize: number = 25) => {
  return useQuery({
    queryKey: ['suppliers', 'page', search, pageNumber, pageSize],
    queryFn: ({ signal }) =>
      supplierService.searchPaged(search, pageNumber, pageSize, signal).then(res => res.data),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
};

// A duplicate code is reported inline by the form, so it needs no toast as well
const isCodeConflict = (error: unknown) => {
  const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
  return response?.status === 409 && /code/i.test(response.data?.detail ?? '');
};

const errorMessage = (error: unknown, fallback: string) => {
  const err = error as { response?: { data?: { detail?: string; title?: string } } };
  return err.response?.data?.detail || err.response?.data?.title || fallback;
};

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSupplierRequest) => supplierService.create(data).then(res => res.data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success(`Supplier "${created.name || created.code}" created`);
    },
    onError: (error: unknown) => {
      if (!isCodeConflict(error)) toast.error(errorMessage(error, 'Failed to create supplier'));
    },
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSupplierRequest }) =>
      supplierService.update(id, data).then(res => res.data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success(`Supplier "${updated.name || updated.code}" updated`);
    },
    onError: (error: unknown) => {
      if (!isCodeConflict(error)) toast.error(errorMessage(error, 'Failed to update supplier'));
    },
  });
};

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => supplierService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('Supplier deleted successfully');
    },
    onError: (error: unknown) => toast.error(errorMessage(error, 'Failed to delete supplier')),
  });
};

// Export the type for use in components
export type { Supplier };
